import { FC } from 'react'

import { Box, Card, Skeleton } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'

/**
 * Placeholder matching one redesigned `DiagnosisCard`: a header row (title +
 * gap pill + chevron), a dumbbell track with the trailing score pair, and two
 * lines of insight copy.
 */
const SkeletonDiagnosisCard: FC = () => (
  <Card variant="outlined" sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 1, borderRadius: borderRadiusDefault }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Skeleton variant="text" width="45%" height={20} sx={{ flex: 1 }} />
      <Skeleton variant="rounded" width={72} height={18} sx={{ borderRadius: 999 }} />
      <Skeleton variant="circular" width={16} height={16} />
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <Skeleton variant="rounded" height={14} sx={{ flex: 1, borderRadius: 999 }} />
      <Skeleton variant="text" width={48} height={18} sx={{ flexShrink: 0 }} />
    </Box>
    <Skeleton variant="text" width="95%" />
    <Skeleton variant="text" width="80%" />
  </Card>
)

/** Placeholder for one diagnosis column: a banner header then a stack of cards. */
const SkeletonDiagnosisColumn: FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Skeleton variant="rounded" height={28} sx={{ borderRadius: borderRadiusDefault }} />
    {Array.from({ length: 3 }).map((_, i) => (
      <SkeletonDiagnosisCard key={i} />
    ))}
  </Box>
)

/**
 * Layout-matched placeholder for `MotivationDiagnosis`: the icon-led portfolio
 * summary paper, the centered signal legend, then a two-column grid of
 * aligned / blind-spots cards.
 */
export const MotivationDiagnosisSkeleton: FC = () => (
  <Box>
    <Card variant="outlined" sx={{ p: 2.25, mb: 2, display: 'flex', gap: 1.75, alignItems: 'flex-start' }}>
      <Skeleton variant="circular" width={36} height={36} sx={{ flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton variant="text" width="35%" height={20} sx={{ mb: 0.5 }} />
        <Skeleton variant="text" width="95%" />
        <Skeleton variant="text" width="85%" />
      </Box>
    </Card>

    {/* Centered signal legend. */}
    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 2 }}>
      {Array.from({ length: 2 }).map((_, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.625 }}>
          <Skeleton variant="circular" width={10} height={10} />
          <Skeleton variant="text" width={96} height={14} />
        </Box>
      ))}
    </Box>

    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <SkeletonDiagnosisColumn />
      <SkeletonDiagnosisColumn />
    </Box>
  </Box>
)
